import { useState } from "react";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { VoiceReportButton } from "@/components/VoiceReportButton";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { eur, timeCost } from "@/lib/format";
import { SignaturePad } from "@/components/SignaturePad";
import { pingNow } from "@/lib/tracking";
import { PartPicker } from "@/components/PartPicker";

export function CloseJobForm({ job, onClosed }) {
  const [summary, setSummary] = useState("");
  const [hours, setHours] = useState("1");
  const [parts, setParts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [signature, setSignature] = useState(null);
  const [signer, setSigner] = useState(`${job.client.first_name} ${job.client.last_name}`);
  const p = job.pricing || {};
  const { cost, minutes } = timeCost(hours, p.hourly_rate || 0, p.billing_fraction_minutes || 30);
  const partsTotal = parts.reduce((s, x) => s + (Number(x.qty) || 0) * (Number(x.unit_price) || 0), 0);
  const gross = Math.round(((p.call_fee || 0) + cost + partsTotal) * 100) / 100;
  const [finalStr, setFinalStr] = useState("");
  const [reason, setReason] = useState("");
  const [suspend, setSuspend] = useState(false);
  const [suspReason, setSuspReason] = useState("");
  const [paid, setPaid] = useState(false);
  const final = finalStr === "" ? gross : Number(finalStr.replace(",", "."));
  const discount = Math.round((gross - final) * 100) / 100;
  const setPart = (i, k, v) => setParts((ps) => ps.map((x, j) => (j === i ? { ...x, [k]: v } : x)));

  const submit = async () => {
    if (summary.trim().length < 3) return toast.error("Inserisci il riepilogo del lavoro svolto");
    if (Number.isNaN(final) || final < 0) return toast.error("Totale finale non valido");
    if (discount < 0) return toast.error("Il totale finale non può superare il totale calcolato");
    if (discount > 0 && reason.trim().length < 3) return toast.error("Indica il motivo dello sconto");
    if (suspend && suspReason.trim().length < 3) return toast.error("Indica il motivo della sospensione (es. in attesa di ricambio)");
    setBusy(true);
    try {
      const { data } = await api.post(`/jobs/${job.id}/${suspend ? "suspend" : "close"}`, {
        ...(suspend ? { reason: suspReason.trim(), paid } : {}),
        summary: summary.trim(), hours: Number(hours) || 0,
        signature, signer_name: signature ? signer.trim() || null : null,
        final_total: discount > 0 ? final : null, discount_reason: discount > 0 ? reason.trim() : null,
        parts: parts.filter((x) => x.name.trim()).map((x) => ({ name: x.name.trim(), qty: Number(x.qty) || 1, unit_price: Number(x.unit_price) || 0, code: x.code || null, catalog: !!x.catalog })),
      });
      toast.success(suspend ? "Intervento sospeso: puoi inviare il rapporto al cliente" : "Intervento chiuso");
      pingNow("job_close", job.id);
      onClosed(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="close-job-form">
      <div className="space-y-2"><div className="flex flex-wrap items-center justify-between gap-2"><Label>Riepilogo lavoro svolto *</Label>
        <VoiceReportButton kind="intervento" value={summary} onText={setSummary} testid="close-summary-voice" /></div>
        <Textarea rows={6} value={summary} onChange={(e) => setSummary(e.target.value)} data-testid="close-summary-input" placeholder="Es. pulizia braciere, sostituzione candeletta, test di funzionamento" /></div>
      <div className="space-y-2"><Label>Ore lavorate</Label>
        <Input type="number" min="0" step="0.25" inputMode="decimal" value={hours} onChange={(e) => setHours(e.target.value)} data-testid="close-hours-input" />
        <p className="text-xs text-slate-500">Fatturate a frazioni di {p.billing_fraction_minutes || 30} min → {minutes} min</p></div>
      <div className="space-y-2">
        <Label>Ricambi utilizzati</Label>
        {parts.map((x, i) => (
          <div key={i} className="grid grid-cols-[1fr_56px_80px_32px] gap-1.5" data-testid={`part-row-${i}`}>
            <PartPicker value={x.name} testid={`part-name-${i}`}
              onText={(v) => setParts((ps) => ps.map((y, j) => (j === i ? { ...y, name: v, catalog: false, code: null } : y)))}
              onPick={(c) => setParts((ps) => ps.map((y, j) => (j === i ? { ...y, name: c.description, unit_price: c.sale_price, code: c.code, catalog: true } : y)))} />
            <Input type="number" min="1" inputMode="decimal" value={x.qty} onChange={(e) => setPart(i, "qty", e.target.value)} data-testid={`part-qty-${i}`} />
            <Input type="number" min="0" step="0.01" inputMode="decimal" placeholder="€" value={x.unit_price} onChange={(e) => setPart(i, "unit_price", e.target.value)} data-testid={`part-price-${i}`} />
            <Button variant="ghost" size="icon" onClick={() => setParts((ps) => ps.filter((_, j) => j !== i))} data-testid={`part-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
        <p className="text-xs text-slate-500">Scegli dal catalogo per avere il prezzo già compilato; se il pezzo non c'è, scrivilo e metti il prezzo a mano (verrà segnalato all'ufficio).</p>
        <Button variant="outline" size="sm" className="rounded-full" onClick={() => setParts((ps) => [...ps, { name: "", qty: 1, unit_price: "" }])} data-testid="part-add-button"><Plus className="h-4 w-4" /> Aggiungi ricambio</Button>
      </div>
      <dl className="space-y-1.5 rounded-2xl bg-[#0f1710] p-4 text-sm text-slate-300" data-testid="close-totals">
        <div className="flex justify-between"><dt>Diritto di chiamata</dt><dd className="font-mono">{eur(p.call_fee)}</dd></div>
        <div className="flex justify-between"><dt>Manodopera ({eur(p.hourly_rate)}/h)</dt><dd className="font-mono">{eur(cost)}</dd></div>
        <div className="flex justify-between"><dt>Ricambi</dt><dd className="font-mono">{eur(partsTotal)}</dd></div>
        {discount > 0 && <div className="flex justify-between border-t border-white/10 pt-2"><dt>Subtotale</dt><dd className="font-mono" data-testid="close-gross-value">{eur(gross)}</dd></div>}
        {discount > 0 && <div className="flex justify-between text-amber-300"><dt>Sconto</dt><dd className="font-mono" data-testid="close-discount-value">-{eur(discount)}</dd></div>}
        <div className="flex justify-between border-t border-white/10 pt-2 text-white"><dt className="font-semibold">Totale</dt><dd className="font-display text-2xl font-bold text-[#a2ca6b]" data-testid="close-total-value">{eur(discount >= 0 && !Number.isNaN(final) ? final : gross)}</dd></div>
      </dl>
      <div className="space-y-2 rounded-2xl border p-4" data-testid="close-discount-box">
        <Label>Totale finale applicato (per fare uno sconto)</Label>
        <Input type="number" min="0" step="0.01" inputMode="decimal" placeholder={String(gross)} value={finalStr} onChange={(e) => setFinalStr(e.target.value)} data-testid="close-final-total-input" />
        <p className="text-xs text-slate-500">Lascia vuoto per applicare il totale calcolato. Lo sconto viene riportato nel rapporto.</p>
        {discount < 0 && <p className="text-xs text-red-600" data-testid="close-final-too-high">Il totale finale non può superare {eur(gross)}</p>}
        {discount > 0 && <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivo dello sconto * (es. cliente abituale)" data-testid="close-discount-reason-input" />}
      </div>
      <div className="space-y-2">
        <Label>Firma del cliente</Label>
        <Input value={signer} onChange={(e) => setSigner(e.target.value)} placeholder="Nome di chi firma" data-testid="signer-name-input" />
        <SignaturePad onChange={setSignature} />
        {!signature && <p className="text-xs text-amber-700" data-testid="signature-missing-hint">Facoltativa: se il cliente non firma, il rapporto riporterà "firma non acquisita".</p>}
      </div>
      <div className="space-y-3 rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="close-suspend-box">
        <label className="flex items-center gap-2 text-sm font-semibold text-amber-900">
          <input type="checkbox" checked={suspend} onChange={(e) => setSuspend(e.target.checked)} className="h-4 w-4" data-testid="close-suspend-toggle" />
          Non riesco a completarlo: sospendi l'intervento</label>
        {suspend && (<>
          <Input value={suspReason} onChange={(e) => setSuspReason(e.target.value)} placeholder="Motivo * (es. in attesa di ricambio)" data-testid="close-suspend-reason" />
          <div className="flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-2"><input type="radio" checked={paid} onChange={() => setPaid(true)} data-testid="close-suspend-paid" /> Pagamento effettuato</label>
            <label className="flex items-center gap-2"><input type="radio" checked={!paid} onChange={() => setPaid(false)} data-testid="close-suspend-unpaid" /> Pagamento sospeso</label>
          </div>
          <p className="text-xs text-amber-800">Il lavoro già fatto e il costo finiscono nel rapporto; l'ufficio riprogramma il completamento.</p>
        </>)}
      </div>
      <Button onClick={submit} disabled={busy} className={`w-full rounded-full h-12 ${suspend ? "bg-amber-600 hover:bg-amber-700" : ""}`} data-testid="close-submit-button">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} {suspend ? "Sospendi intervento e genera rapporto" : "Chiudi intervento e genera rapporto"}
      </Button>
    </div>
  );
}
